import { toast } from "react-toastify";
import { useCurrentUser, useLogout, useRefreshUser } from "utils/hooks";
import { useDispatch } from "react-redux";
import { clearOrganizationDataAction } from "store/actions/organizations";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganizationSwitch } from "contexts/OrganizationSwitchContext";

const useSwitchOrganization = () => {
  const { logOut } = useLogout();
  const { user } = useCurrentUser();
  const { submitRefreshRequest } = useRefreshUser();
  const dispatch = useDispatch();
  const [isSwitching, setIsSwitching] = useState(false);
  const navigate = useNavigate();
  const { setIsSwitching: setGlobalSwitching } = useOrganizationSwitch();

  const handleSwitchOrganization = async (organizationId: string) => {
    if (!organizationId) {
      return null;
    }

    setIsSwitching(true);
    setGlobalSwitching(true);

    // Set a timeout to ensure loading screen doesn't get stuck
    const timeoutId = setTimeout(() => {
      console.warn("Organization switch timeout - forcing completion");
      setIsSwitching(false);
      setGlobalSwitching(false);
      navigate("/dashboard");
    }, 10000); // 10 second timeout

    return new Promise<void>((resolve) => {
      fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
            mutation {
              switchOrganization(organizationId: "${organizationId}") {
                uuid
                name
              }
            }
          `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: user?.activeOrganizationId || "NA",
          permissionKey: "switch_organization",
        },
      })
        .then(async (res) => {
          if (res.status !== 200 && res.status !== 201) {
            const error = await res.json();
            if (error.errors[0].message.includes("BRKN_6001")) {
              toast.warn(`For security purposes please log in again.`);
              logOut();
            } else {
              toast.error(`Hmm, something went wrong.`);
              throw new Error("Failed!");
            }
          }
          return res.json();
        })
        .then(async (res) => {
          if (res.data?.switchOrganization) {
            toast.success("Organization switched successfully");

            // Clear organization-specific data from Redux store first
            dispatch(clearOrganizationDataAction());

            // Refresh user data to get updated activeOrganizationId
            await submitRefreshRequest();

            // Wait a bit to ensure the user data is updated and Redux store is cleared
            setTimeout(() => {
              // Clear the timeout since we're proceeding normally
              clearTimeout(timeoutId);

              setIsSwitching(false);
              setGlobalSwitching(false);
            }, 50); // Reduced delay for better responsiveness
          } else {
            clearTimeout(timeoutId);
            setIsSwitching(false);
            setGlobalSwitching(false);
          }
          resolve();
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          console.error("Error switching organization:", err);
          toast.error("Failed to switch organization");
          setIsSwitching(false);
          setGlobalSwitching(false);
          resolve();
        });
    });
  };

  return {
    switchOrganization: handleSwitchOrganization,
    isSwitching,
  };
};

export default useSwitchOrganization;
